import {NetworkStatus} from 'apollo-client';
import {useMemo} from 'react';

import {useConfigValuesCategoriesQueryQuery} from '../../../../../generated/graphql';
import * as QueryTS from '../../../../../util/queryts';
import {Sort} from '../../../../../util/queryTypes';
import {ConfigValueForRun} from './useConfigValuesAggregation';

export function useConfigValuesCategories({
  currentKey,
  entityName,
  filters,
  order,
  projectName,
  shouldSkip,
}: {
  currentKey?: string;
  entityName: string;
  filters: string;
  order: Sort;
  projectName: string;
  shouldSkip: boolean;
}): {
  configValuesForRun: ConfigValueForRun[];
  configValuesCategories: string[];
  configValuesError?: Error;
  configValuesLoading: boolean;
} {
  // Get the config values for each run
  const {
    data,
    error: configValuesError,
    networkStatus,
  } = useConfigValuesCategoriesQueryQuery({
    context: {
      propagateErrors: true,
    },
    variables: {
      filters: filters,
      limit: 1000,
      order: order ? QueryTS.sortToOrderString(order) : '-createdAt',
      configKey: currentKey?.replace('config.', '') ?? '', // this cannot be hit because the query will be skipped when `currentKey` doesn't exist
      configType: 'string',
      entityName,
      projectName,
    },
    notifyOnNetworkStatusChange: true,
    skip: shouldSkip,
  });
  const configValuesLoading = networkStatus !== NetworkStatus.ready;

  const {configValuesForRun, configValuesCategories} = useMemo(() => {
    try {
      const configValuesForRun: ConfigValueForRun[] = [];
      for (const [key, value] of Object.entries(
        data?.project?.runs?.configValues ?? {}
      )) {
        configValuesForRun.push({
          runName: key,
          value: Array.isArray(value) ? `[${value.join(' ')}]` : `${value}`,
        });
      }
      return {
        configValuesForRun,
        configValuesCategories:
          data?.project?.runs?.configValuesCategories ?? [],
      };
    } catch (e) {
      console.error(e);
      return {
        configValuesForRun: [],
        configValuesCategories: [],
      };
    }
  }, [data]);

  return useMemo(
    () => ({
      configValuesForRun,
      configValuesCategories,
      configValuesError,
      configValuesLoading,
    }),
    [
      configValuesForRun,
      configValuesCategories,
      configValuesError,
      configValuesLoading,
    ]
  );
}
